import { put, select, takeEvery, take, call } from '@redux-saga/core/effects';
import { prepareFiltersReverse } from 'models/filter';
import { generateSearchParametersReverse } from 'store/utils';
import { reportMessage } from 'lib/error';

import { getAccessDefinitionOptionsQueryState } from 'services/svp-api-client/access-definitions';
import { PLAYLISTS_FETCH_SUCCESS } from 'store/playlists/items';
import { PLAYLISTS_FILTERS_SET_CONTROLLER, playlistsFiltersOverride } from 'store/playlists/filters';

import { getPlaylist, isPlaylistLoaded } from 'store/playlists/selectors';
import { getCategories } from 'store/categories/selectors';
import { fetchCategory } from 'store/categories/sagas';
import { adminBffSdk } from 'services/admin-bff-sdk';

export function* fetchTagsForPlaylist({ provider, playlistTagsIds }) {
    try {
        if (!playlistTagsIds.length) {
            return undefined;
        }

        const tagsPromise = yield put(adminBffSdk.endpoints.ListTags.initiate({ provider, ids: playlistTagsIds }));
        const { data, error } = yield tagsPromise;

        if (error) {
            throw new Error(error);
        }

        return Object.fromEntries((data?.listTags || []).map(({ id, title }) => [id, title]));
    } catch (error) {
        reportMessage({
            message: 'Failed to fetch missing tags',
            error,
        });
        return undefined;
    }
}

export function* fetchStoriesForPlaylist({ provider, playlistStoriesIds }) {
    try {
        if (!playlistStoriesIds) {
            return undefined;
        }

        const storiesPromise = yield put(adminBffSdk.endpoints.Stories.initiate({ provider, ids: playlistStoriesIds }));
        const { data, error } = yield storiesPromise;

        if (error) {
            throw new Error(error);
        }

        return Object.fromEntries((data?.stories || []).map(({ id, title }) => [id, title]));
    } catch (error) {
        reportMessage({
            message: 'Failed to fetch missing stories',
            error,
        });
        return undefined;
    }
}

export function* setFiltersController({ payload: { provider, playlistId } }) {
    const shouldWaitForPlaylist = yield select((state) => !isPlaylistLoaded(state, provider, playlistId));

    if (shouldWaitForPlaylist) {
        yield take(PLAYLISTS_FETCH_SUCCESS);
    }

    const playlist = yield select((state) => getPlaylist(state, provider, playlistId));
    const searchQuery = new URLSearchParams(playlist.searchQuery || '');
    const before = Number(searchQuery.get('before'));
    const after = Number(searchQuery.get('after'));
    const newsrooms = searchQuery.get('searchQueryProviders');

    const preparedFilters = { ...prepareFiltersReverse(searchQuery.get('filter') || '') };
    const allTags = [preparedFilters?.['additional.tags.id'], preparedFilters?.excludedTags].flat().filter(Boolean);

    const { categoryId, excludedCategory } = preparedFilters;
    if (categoryId) {
        yield call(fetchCategory, { payload: { provider, categoryId } });
    }
    if (excludedCategory?.length) {
        for (const excludedId of excludedCategory) {
            yield call(fetchCategory, { payload: { provider, categoryId: excludedId } });
        }
    }
    const categories = yield select((state) => getCategories(state, { provider }));

    const tags = yield call(fetchTagsForPlaylist, {
        provider,
        playlistTagsIds: allTags,
    });
    const stories = yield call(fetchStoriesForPlaylist, {
        provider,
        playlistStoriesIds: preparedFilters?.['additional.stories'],
    });

    const accessDefinitionOptions = yield select(getAccessDefinitionOptionsQueryState(provider));

    const filters = {
        ...generateSearchParametersReverse(
            provider,
            newsrooms,
            preparedFilters,
            tags,
            stories,
            categories,
            accessDefinitionOptions
        ),
        ...(before && { before }),
        ...(after && { after }),
    };
    yield put(playlistsFiltersOverride({ filters }));
}

export default [takeEvery(PLAYLISTS_FILTERS_SET_CONTROLLER, setFiltersController)];
